import { AloftPriceJSON } from '@aloft/shared-objects';
import {
  AloftArea,
  AloftPrice,
  Improvements,
  ManufacturedHomeFeatures,
  ManufacturedHomeFoundationDisplay,
  SubjectData,
  isDefined,
} from '@aloft/toolkit-shared';

import { makeStreetViewStaticURL } from '../../../hooks/googleMaps/makeStreetViewStaticURL.ts';
import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { FeatureArea } from '../types.ts';

export const getSubjectPropertyImageUrl = (subject?: SubjectData): string | undefined =>
  subject?.readonlyData.backupListingData?.listing.Media?.[0]?.MediaURL;

export const formatFeatureArea = ({ area, unit, type, hasFeature }: FeatureArea) => {
  if (!area || !unit || !hasFeature) return '';
  const aloftArea = AloftArea.fromNumber(area, unit).toString(true);
  return type ? `${aloftArea} ${type}` : aloftArea;
};

export const formatFeatureAreas = (areas?: FeatureArea[]) => {
  if (!areas || areas.length === 0) return '';
  return areas.map(formatFeatureArea).filter(Boolean).join('; ');
};

export const getFoundationDisplay = (improvements: Improvements | undefined): string => {
  let foundationLabel: ManufacturedHomeFoundationDisplay | undefined;
  if (improvements?.propertyType === 'manufacturedHome') {
    const { foundation } = improvements as ManufacturedHomeFeatures;
    foundationLabel = foundation as ManufacturedHomeFoundationDisplay;
  }
  return foundationLabel ? ` (${foundationLabel})` : '';
};

export const getBathCountAdjustment = (adjustments: CompAdjustments | undefined): string => {
  const bathAboveGradeAdjustment = adjustments?.bathAboveGrade?.adjustment;
  const bathBelowGradeAdjustment = adjustments?.bathBelowGrade?.adjustment;
  let bathCountAdjustment = '';
  if (bathAboveGradeAdjustment && bathBelowGradeAdjustment) {
    const total = bathAboveGradeAdjustment.add(bathBelowGradeAdjustment);
    bathCountAdjustment = total ? AloftPrice.schema.parse(total).toString(true) : '';
  } else if (bathAboveGradeAdjustment) {
    bathCountAdjustment = AloftPrice.schema.parse(bathAboveGradeAdjustment).toString(true);
  } else if (bathBelowGradeAdjustment) {
    bathCountAdjustment = AloftPrice.schema.parse(bathBelowGradeAdjustment).toString(true);
  }
  return bathCountAdjustment;
};

export const formatAdjustmentPrice = (price: AloftPriceJSON | undefined): string =>
  price ? AloftPrice.schema.parse(price).toString(true) : '';

export const getDistanceToSubject = (
  to?: { latitude?: number; longitude?: number },
  from?: { latitude?: number; longitude?: number },
): string | undefined =>
  isDefined(to) && isDefined(from)
    ? (+(proximityMiles(to, from)?.toFixed(2) ?? 0)).toLocaleString()
    : undefined;

export const getPropertyImage = (
  mlsPhotoUrl: string | undefined,
  coordinates: { latitude?: number; longitude?: number } | undefined,
) => {
  if (mlsPhotoUrl) {
    return mlsPhotoUrl;
  }
  if (coordinates?.latitude && coordinates?.longitude)
    return makeStreetViewStaticURL(coordinates.latitude, coordinates.longitude);
  return undefined;
};

export const getPropertyImageWithFallback = async (
  mlsPhotoUrl: string | undefined,
  coordinates: { latitude?: number; longitude?: number } | undefined,
): Promise<string | undefined> => {
  if (mlsPhotoUrl) {
    try {
      // Try to fetch the image to check if it's accessible
      const response = await fetch(mlsPhotoUrl, {
        method: 'HEAD',
        mode: 'cors',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) {
        return mlsPhotoUrl;
      }
    } catch {
      if (coordinates?.latitude && coordinates?.longitude) {
        return makeStreetViewStaticURL(coordinates.latitude, coordinates.longitude);
      }
    }
  }

  // If no MLS photo, try Street View
  if (coordinates?.latitude && coordinates?.longitude) {
    return makeStreetViewStaticURL(coordinates.latitude, coordinates.longitude);
  }

  return undefined;
};
